import React, { useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FaArrowLeft, FaArrowRight, FaPrint } from 'react-icons/fa6'
import { useSelector } from 'react-redux'
import moment from 'moment'
import { amountToWords } from '../../thirdParty/AmountToWord'
import SharedButton from '../../components/Shared/SharedButton'

export default function ViewPurchasedEntryPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t, i18n } = useTranslation()
  const isRTL = i18n.dir() === "rtl"
  const language = i18n.language
  const { user } = useSelector((state) => state.auth)
  const purchaseEntry = location?.state?.purchaseEntry || null
  const invoiceRef = useRef(null)

  if (!purchaseEntry) {
    return (
      <div className="p-4">
        <div className='flex gap-3 mb-4'>
          <SharedButton
            onClick={() => navigate(-1)}
            icon={isRTL ? <FaArrowRight /> : <FaArrowLeft />}
            label={t("Back")}
          />
        </div>
        <p className='mt-4'>{t('No_purchase_selected')}</p>
      </div>
    )
  }

  const data = purchaseEntry.purchase_detail || []
  const vendor = purchaseEntry.vendor || {}

  const totalQty = data.reduce((acc, item) => acc + (Number(item.quantity) || 0), 0)

  const totalPublicPrice = data.reduce((acc, item) => acc + ((Number(item.item_price) || 0) * (Number(item.quantity) || 0)), 0)

  const totalDiscount = data.reduce((acc, item) => {
    const rawTotal = (Number(item.item_price) || 0) * (Number(item.quantity) || 0)
    const subtotal = Number(item.subtotal) || 0
    return acc + (rawTotal - subtotal)
  }, 0)

  const totalInvoiceAmount = data.reduce((acc, item) => acc + (Number(item.subtotal) || 0), 0)

  const totalVat = data.reduce((acc, item) => acc + (Number(item.vat_amount) || 0), 0)
  const grandTotal = data.reduce((acc, item) => acc + (Number(item.total) || 0), 0)

  const handlePrint = () => {
    const printContents = invoiceRef.current.innerHTML;
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow.document;

    doc.open();
    doc.write(`
      <html>
        <head>
          <title>Purchase Entry ${purchaseEntry.entry_number}</title>
          <script src="https://cdn.tailwindcss.com"></script>
          <style>
            body { font-family: Arial, sans-serif; padding: 10px; }
            @media print {
              .no-print { display: none !important; }
              body { -webkit-print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          ${printContents}
        </body>
      </html>
    `);
    doc.close();
    iframe.onload = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    };
  };

  const formatAmountInWords = (amount) => {
    if (!amount) return "";
    const amountStr = Number(amount).toFixed(2);
    const [integer, decimal] = amountStr.split('.');
    const integerWords = amountToWords(Number(integer));
    // Capitalize first letter
    const capitalized = integerWords.charAt(0).toUpperCase() + integerWords.slice(1);

    // Format "and 38/100"
    const decimalPart = decimal ? `${decimal}/100` : "00/100";
    return `${capitalized} and ${decimalPart} SAR`;
  }

  const amountInWords = formatAmountInWords(grandTotal);

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      <div className="flex gap-3 mb-4 no-print container mx-auto max-w-4xl">
        <SharedButton
          onClick={() => navigate('/manager/purchased_entry')}
          icon={isRTL ? <FaArrowRight /> : <FaArrowLeft />}
          label={t("Back")}
        />
        <SharedButton
          onClick={handlePrint}
          icon={<FaPrint />}
          label={t("PrintInvoice")}
        />
      </div>

      <div ref={invoiceRef}>
        <div className="max-w-4xl mx-auto bg-white border border-[#333] rounded shadow-lg p-0 mb-8">

          <div className="flex items-center justify-between px-4 py-2">
            <div className="flex-1 flex flex-col items-center">
              <h2 className="text-[18px] font-bold text-gray-800 uppercase tracking-wide">
                PURCHASE ENTRY
              </h2>
              <h3 className="text-lg text-gray-700 font-bold">
                إدخال المشتريات
              </h3>
            </div>
          </div>

          <div className="mb-4">
            <table className="w-full border-collapse">
              <tbody>
                <tr>
                  <td className="!w-[210px] text-[12px] border px-[2px] border-[#333] font-bold border-l-0 bg-gray-50">
                    Vendor Name
                  </td>
                  <td className="text-[12px] border px-[2px] border-[#333] text-center font-semibold">
                    {vendor.name_en || vendor.name || '-'}
                  </td>
                  <td className="!w-[210px] text-[12px] border px-[2px] border-[#333] text-right border-r-0 font-bold bg-gray-50">
                    اسم المورد
                  </td>
                </tr>
                <tr>
                  <td className="!w-[210px] text-[12px] border px-[2px] border-[#333] font-medium border-l-0">
                    Purchase Date
                  </td>
                  <td className="text-[12px] border px-[2px] border-[#333] text-center">
                    {purchaseEntry.purchase_date ? moment(purchaseEntry.purchase_date).format("DD-MM-YYYY") : '-'}
                  </td>
                  <td className="!w-[210px] text-[12px] border px-[2px] border-[#333] text-right border-r-0 text-right">
                    تاريخ الشراء
                  </td>
                </tr>
                <tr>
                  <td className="!w-[210px] text-[12px] border px-[2px] border-[#333] font-medium border-l-0">
                    Entry Number
                  </td>
                  <td className="text-[12px] border px-[2px] border-[#333] text-center">
                    {purchaseEntry.entry_number || '-'}
                  </td>
                  <td className="!w-[210px] text-[12px] border px-[2px] border-[#333] text-right border-r-0 text-right">
                    رقم القيد
                  </td>
                </tr>
                <tr>
                  <td className="!w-[210px] text-[12px] border px-[2px] border-[#333] font-medium border-l-0">
                    Vendor VAT Number
                  </td>
                  <td className="text-[12px] border px-[2px] border-[#333] text-center">
                    {vendor.vat_no || '-'}
                  </td>
                  <td className="!w-[210px] text-[12px] border px-[2px] border-[#333] text-right border-r-0 text-right">
                    الرقم الضريبي للمورد
                  </td>
                </tr>
                <tr>
                  <td className="!w-[210px] text-[12px] border px-[2px] border-[#333] font-medium border-l-0">
                    Vendor CR Number
                  </td>
                  <td className="text-[12px] border px-[2px] border-[#333] text-center">
                    {vendor.cr_no || '-'}
                  </td>
                  <td className="!w-[210px] text-[12px] border px-[2px] border-[#333] text-right border-r-0 text-right">
                    السجل التجاري للمورد
                  </td>
                </tr>
                <tr>
                  <td className="!w-[210px] text-[12px] border px-[2px] border-[#333] font-medium border-l-0">
                    Invoice Number
                  </td>
                  <td className="text-[12px] border px-[2px] border-[#333] text-center">
                    {purchaseEntry.refrence || '-'}
                  </td>
                  <td className="!w-[210px] text-[12px] border px-[2px] border-[#333] text-right border-r-0 text-right">
                    رقم الفاتورة
                  </td>
                </tr>
                <tr>
                  <td className="!w-[210px] text-[12px] border px-[2px] border-[#333] font-medium border-l-0">
                    Notes
                  </td>
                  <td className="text-[12px] border px-[2px] border-[#333] text-center">
                    {purchaseEntry.notes || '-'}
                  </td>
                  <td className="!w-[210px] text-[12px] border px-[2px] border-[#333] text-right border-r-0 text-right">
                    ملاحظات
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="flex items-stretch border border-[#333] border-l-0 border-r-0 mb-4">
            <div className="flex items-center justify-center bg-gray-50 border-r border-[#333]">
              <p className="text-[12px] font-bold px-2 w-[80px] text-center">
                Vendor Address
              </p>
            </div>
            <div className="flex-1">
              <table className="w-full border-collapse">
                <tbody>
                  <tr>
                    <td className="!w-[140px] text-[12px] border-b border-[#333] px-2 py-1 font-medium">
                      Address
                    </td>
                    <td className="text-center text-[12px] border-l border-r border-b border-[#333] px-2 py-1">
                      {vendor.street || '-'}
                    </td>
                    <td className="!w-[140px] text-[12px] border-b border-[#333] px-2 py-1 text-right font-medium">
                      العنوان
                    </td>
                  </tr>
                  <tr>
                    <td className="!w-[140px] text-[12px] border-b border-[#333] px-2 py-1 font-medium">
                      City / Country
                    </td>
                    <td className="text-center text-[12px] border-l border-r border-b border-[#333] px-2 py-1">
                      {[vendor.city, vendor.country].filter(Boolean).join(' / ') || '-'}
                    </td>
                    <td className="!w-[140px] text-[12px] border-b border-[#333] px-2 py-1 text-right font-medium">
                      المدينة / البلد
                    </td>
                  </tr>
                  <tr>
                    <td className="!w-[140px] text-[12px] border-b border-[#333] px-2 py-1 font-medium">
                      Postal Code
                    </td>
                    <td className="text-center text-[12px] border-l border-r border-b border-[#333] px-2 py-1">
                      {vendor.postal_code || '-'}
                    </td>
                    <td className="!w-[140px] text-[12px] border-b border-[#333] px-2 py-1 text-right font-medium">
                      الرمز البريدي
                    </td>
                  </tr>
                  <tr>
                    <td className="text-[12px] px-2 py-1 font-medium">
                      Contact
                    </td>
                    <td className="text-center text-[12px] border-l border-r border-[#333] px-2 py-1">
                      {[vendor.phone, vendor.email].filter(Boolean).join(' | ') || '-'}
                    </td>
                    <td className="text-[12px] px-2 py-1 text-right font-medium">
                      اتصل
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-center bg-gray-50 border-l border-[#333]">
              <p className="text-[12px] font-bold px-2 w-[80px] text-center">
                عنوان المورد
              </p>
            </div>
          </div>
          <div className="mt-4">
            <table className="w-full border-collapse border-y border-[#333]">
              <thead>
                <tr className="bg-gray-200">
                  <th className="border border-[#333] px-2 py-2 text-[12px] w-[5%] font-bold">#</th>
                  <th className="border border-[#333] px-2 py-2 text-[12px] font-bold">Item Code / رمز الصنف</th>
                  <th className={`border border-[#333] px-2 py-2 text-[12px] w-[25%] font-bold ${language === 'ar' ? 'text-right' : 'text-left'}`}>Item Name / اسم الصنف</th>
                  <th className="border border-[#333] px-2 py-2 text-[12px] font-bold">Batch / رقم التشغيلة</th>
                  <th className="border border-[#333] px-2 py-2 text-[12px] font-bold">Expiry / الانتهاء</th>
                  <th className="border border-[#333] px-2 py-2 text-[12px] font-bold">Qty / الكمية</th>
                  <th className="border border-[#333] px-2 py-2 text-[12px] font-bold">Bonus / بونص</th>
                  <th className="border border-[#333] px-2 py-2 text-[12px] font-bold">Price / السعر</th>
                  <th className="border border-[#333] px-2 py-2 text-[12px] font-bold">Discount / الخصم</th>
                  <th className="border border-[#333] px-2 py-2 text-[12px] font-bold">VAT / الضريبة</th>
                  <th className="border border-[#333] px-2 py-2 text-[12px] font-bold">Total / المجموع</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item, index) => (
                  <tr key={index} className="even:bg-gray-50">
                    <td className="border border-[#333] px-2 py-1 text-center text-[12px]">{index + 1}</td>
                    <td className="border border-[#333] px-2 py-1 text-center text-[12px]">{item.item_code || '-'}</td>
                    <td className="border border-[#333] px-2 py-1 text-[12px] font-medium">{language === 'ar' ? item.item_name_ar || item?.item_name_en : item.item_name_en}</td>
                    <td className="border border-[#333] px-2 py-1 text-center text-[12px]">{item.batch_number || '-'}</td>
                    <td className="border border-[#333] px-2 py-1 text-center text-[12px]">{item.expiry_date ? moment(item.expiry_date).format('DD-MM-YYYY') : '-'}</td>
                    <td className="border border-[#333] px-2 py-1 text-center text-[12px]">{item.quantity}</td>
                    <td className="border border-[#333] px-2 py-1 text-center text-[12px]">{item.bonus || 0}</td>
                    <td className="border border-[#333] px-2 py-1 text-center text-[12px]">{Number(item.item_price).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td className="border border-[#333] px-2 py-1 text-center text-[12px]">
                      {(((Number(item.item_price) || 0) * (Number(item.quantity) || 0)) - (Number(item.subtotal) || 0)).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="border border-[#333] px-2 py-1 text-center text-[12px]">{item.vat_percent}%</td>
                    <td className="border border-[#333] px-2 py-1 text-center text-[12px] font-semibold">{Number(item.total).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
                {data.length === 0 && (
                  <tr>
                    <td colSpan="11" className="text-center py-4 text-gray-500 border border-[#333]">No items found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end mt-4 px-4 mb-4">
            <div className="w-full md:w-1/2 lg:w-1/2">
              <table className="w-full border-collapse border border-[#333]">
                <tbody>
                  <tr>
                    <td className="border border-[#333] px-3 py-2 text-[12px] font-semibold bg-gray-50 w-1/2">{t('Total Qty')}</td>
                    <td className="border border-[#333] px-3 py-2 text-[12px] text-right font-semibold w-1/2">{totalQty}</td>
                  </tr>
                  <tr>
                    <td className="border border-[#333] px-3 py-2 text-[12px] font-semibold bg-gray-50">{t('Total Unit Price')}</td>
                    <td className="border border-[#333] px-3 py-2 text-[12px] text-right font-semibold">{totalPublicPrice.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  </tr>
                  <tr>
                    <td className="border border-[#333] px-3 py-2 text-[12px] font-semibold bg-gray-50">{t('Total Discount Amount')}</td>
                    <td className="border border-[#333] px-3 py-2 text-[12px] text-right font-semibold">{totalDiscount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  </tr>
                  <tr>
                    <td className="border border-[#333] px-3 py-2 text-[12px] font-semibold bg-gray-50">{t('Net Amount')}</td>
                    <td className="border border-[#333] px-3 py-2 text-[12px] text-right font-semibold">{totalInvoiceAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  </tr>
                  <tr>
                    <td className="border border-[#333] px-3 py-2 text-[12px] font-semibold bg-gray-50">{t('total_vat')}</td>
                    <td className="border border-[#333] px-3 py-2 text-[12px] text-right font-semibold">{totalVat.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  </tr>
                  <tr className="bg-gray-200">
                    <td className="border border-[#333] px-3 py-2 text-[14px] font-semibold">{t('Grand Total')}</td>
                    <td className="border border-[#333] px-3 py-2 text-[14px] font-semibold text-right">{grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  </tr>
                </tbody>
              </table>
              <div className="mt-4 text-right">
                <p className="font-semibold text-[13px] underline mb-1">{t('Amount in Words')}:</p>
                <p className="text-[13px] italic font-medium">{amountInWords} ({t('approx — partially blurred')})</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
